import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Platform, Alert, Linking, StyleProp, ViewStyle } from 'react-native';
import Sound from 'react-native-sound';
import Tts from 'react-native-tts';
import { useAudioPlayback } from '../hooks/useAudioPlayback';

// Module-level guard: only show the "install TTS" Alert once per app session.
// Without this every tap on a missing-engine device would redirect to the
// Play Store, which is exactly the trapped state the user reported.
let __ttsInstallPrompted = false;

// Diagnostic tag — filter logcat with:
//   adb logcat ReactNativeJS:V *:S | grep EduWrite-TTS
const TTS_LOG = '[EduWrite-TTS]';

// One-time diagnostic dump of the TTS environment. Runs lazily on first
// init failure / first speak so logcat shows engine state when needed.
let __ttsDiagnosticsLogged = false;
const logTtsDiagnostics = async (context: string) => {
    if (__ttsDiagnosticsLogged) return;
    __ttsDiagnosticsLogged = true;
    console.log(`${TTS_LOG} === Diagnostics (${context}) ===`);
    console.log(`${TTS_LOG} Platform: ${Platform.OS} ${Platform.Version}`);
    try {
        const engines = await (Tts as any).engines?.();
        console.log(`${TTS_LOG} engines():`, JSON.stringify(engines));
    } catch (e: any) {
        console.log(`${TTS_LOG} engines() threw: ${e?.message || e} code=${e?.code}`);
    }
    try {
        const voices = await (Tts as any).voices?.();
        console.log(`${TTS_LOG} voices() count: ${Array.isArray(voices) ? voices.length : 'n/a'}`);
        if (Array.isArray(voices) && voices.length) {
            console.log(`${TTS_LOG} first voice:`, JSON.stringify(voices[0]));
        }
    } catch (e: any) {
        console.log(`${TTS_LOG} voices() threw: ${e?.message || e} code=${e?.code}`);
    }
};

/**
 * Try to recover from a TTS init failure by enumerating installed engines
 * and selecting one. Returns true if an engine was successfully set as default.
 */
const tryRecoverTtsEngine = async (): Promise<boolean> => {
    try {
        const engines: Array<{ name: string; label?: string; default?: boolean }> =
            (await (Tts as any).engines?.()) || [];
        console.log(`${TTS_LOG} recover: engines found=${engines.length}`, JSON.stringify(engines));
        if (!engines.length) return false;
        const preferred = engines.find((e) => e.default) || engines[0];
        if (!preferred?.name) {
            console.log(`${TTS_LOG} recover: no preferred engine name`);
            return false;
        }
        console.log(`${TTS_LOG} recover: setDefaultEngine("${preferred.name}")`);
        await (Tts as any).setDefaultEngine?.(preferred.name);
        // Re-check init after switching engines
        await Tts.getInitStatus();
        console.log(`${TTS_LOG} recover: init OK after setDefaultEngine`);
        return true;
    } catch (e: any) {
        console.log(`${TTS_LOG} recover: failed code=${e?.code} message=${e?.message}`);
        return false;
    }
};

/**
 * Surface a clear, single-shot dialog explaining what's wrong and how to fix it.
 * Three buttons:
 *  - Open Settings: jumps to Android TTS settings so the user can pick a default
 *  - Install: opens the Play Store entry for the engine
 *  - Cancel
 */
const showTtsInstallHelp = (errCode: string | undefined) => {
    if (__ttsInstallPrompted) return;
    __ttsInstallPrompted = true;
    const message =
        errCode === 'no_engine'
            ? 'No text-to-speech engine is installed.\n\n' +
              '1. Tap "Install" to download "Speech Recognition & Synthesis" from the Play Store.\n' +
              '2. After install, open Settings → System → Languages → Text-to-speech output.\n' +
              '3. Choose "Speech Recognition & Synthesis" as the preferred engine.\n' +
              '4. Restart this app.'
            : 'Text-to-speech is installed but not yet selected as the default engine.\n\n' +
              'Open Settings → System → Languages → Text-to-speech output and choose your installed engine, then restart the app.';
    Alert.alert('Listen needs setup', message, [
        { text: 'Cancel', style: 'cancel' },
        {
            text: 'Open Settings',
            onPress: () => {
                // Android intent to TTS settings; falls back to general settings
                Linking.sendIntent?.('com.android.settings.TTS_SETTINGS').catch(() => {
                    Linking.openSettings();
                });
            },
        },
        ...(errCode === 'no_engine'
            ? [{
                  text: 'Install',
                  onPress: () => { try { Tts.requestInstallEngine(); } catch { /* noop */ } },
              }]
            : []),
    ]);
};

interface AudioPromptButtonProps {
    text: string;
    audioUrl?: string;
    /** Auto-play audio once on mount (EYFS-only). */
    autoPlay?: boolean;
    /** Compact icon-only pill mode for narrow headers and mobile screens. */
    compact?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

/**
 * Imperative one-shot speech utility — for surfaces that need to speak
 * text in response to a tap *without* rendering a button (e.g. RosterPicker
 * speaking a child's name on first tap before committing the selection).
 *
 * Mirrors AudioPromptButton playback logic: pre-recorded audioUrl preferred,
 * Web Speech API fallback on web, no-op on native if neither is available.
 *
 * Voice-first contract: this function MUST never block the caller — it
 * returns immediately and audio plays asynchronously. Callers should not
 * `await` it; UI actions (commit, navigate) proceed independently.
 */
export const stopTextToSpeech = (): void => {
    try {
        if (Platform.OS === 'web') (globalThis as any).speechSynthesis?.cancel?.();
        else Tts.stop();
    } catch (stopError) {
        console.warn('[AudioPromptButton] Failed to stop text-to-speech:', stopError);
    }
};

export const speakText = (text: string, audioUrl?: string): void => {
    if (!text && !audioUrl) return;
    try {
        if (audioUrl) {
            const sound = new Sound(audioUrl, '', (error) => {
                if (error) {
                    console.warn('[speakText] Failed to load sound:', error);
                    return;
                }
                sound.play(() => sound.release());
            });
            return;
        }
        if (Platform.OS === 'web' && typeof globalThis !== 'undefined' && (globalThis as any).speechSynthesis) {
            const synth = (globalThis as any).speechSynthesis;
            // Cancel any pending utterances so a fast second tap doesn't queue.
            synth.cancel?.();
            const utterance = new (globalThis as any).SpeechSynthesisUtterance(text);
            // Slower rate for children (default 1.0 is often too fast).
            utterance.rate = 0.7;
            synth.speak(utterance);
            return;
        }
        if (Platform.OS !== 'web') {
            Tts.stop();
            Tts.setDefaultRate(0.65);
            Tts.speak(text);
        }
    } catch (e) {
        console.warn('[speakText] Failed to speak:', e);
    }
};

/**
 * AudioPromptButton — reusable atom for reading prompts aloud.
 */
export const AudioPromptButton: React.FC<AudioPromptButtonProps> = ({
    text,
    audioUrl,
    autoPlay = false,
    compact = false,
    style,
    testID = 'audio-prompt-button',
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [audioError, setAudioError] = useState<string | null>(null);
    const isPlayingRef = useRef(false);
    const soundRef = useRef<Sound | null>(null);
    const animationRef = useRef<Animated.CompositeAnimation | null>( null);
    const wave1 = useRef(new Animated.Value(0)).current;
    const wave2 = useRef(new Animated.Value(0)).current;
    const wave3 = useRef(new Animated.Value(0)).current;
    const autoPlayedRef = useRef(false);
    const ttsReadyRef = useRef(Platform.OS === 'web');

    // WP-055: useAudioPlayback for the audioUrl playback path.
    // Replaces the bare console.warn + silent state reset with visible error state.
    const audioPlayback = useAudioPlayback(audioUrl || '', () => {
        setIsPlaying(false);
        stopWaveAnimation();
    });

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        if (Platform.OS === 'web') return;
        let cancelled = false;
        console.log(`${TTS_LOG} prewarm: calling getInitStatus()`);
        Tts.getInitStatus()
            .then(() => {
                if (cancelled) return;
                ttsReadyRef.current = true;
                console.log(`${TTS_LOG} prewarm: init OK, rate set to 0.65`);
                Tts.setDefaultRate(0.65);
            })
            .catch(async (err) => {
                if (cancelled) return;
                console.warn(`${TTS_LOG} prewarm getInitStatus() failed:`, err?.code, err?.message);
                logTtsDiagnostics('prewarm_failed');
                const recovered = await tryRecoverTtsEngine();
                if (recovered) {
                    ttsReadyRef.current = true;
                    console.log(`${TTS_LOG} prewarm: auto-recovery succeeded`);
                    Tts.setDefaultRate(0.65);
                } else {
                    console.log(`${TTS_LOG} prewarm: auto-recovery failed (will prompt user on tap)`);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        return () => {
            soundRef.current?.release();
            animationRef.current?.stop();
        };
    }, []);

    const startWaveAnimation = () => {
        const createWave = (val: Animated.Value, delay: number) =>
            Animated.loop(
                Animated.sequence([
                    Animated.timing(val, { toValue: 1, duration: 400, delay, useNativeDriver: true }),
                    Animated.timing(val, { toValue: 0.2, duration: 400, useNativeDriver: true }),
                ])
            );

        animationRef.current = Animated.parallel([
            createWave(wave1, 0),
            createWave(wave2, 150),
            createWave(wave3, 300),
        ]);
        animationRef.current.start();
    };

    const stopWaveAnimation = () => {
        animationRef.current?.stop();
        wave1.setValue(0);
        wave2.setValue(0);
        wave3.setValue(0);
    };

    const playAudio = async () => {
        if (!text && !audioUrl) return;

        // WP-055: Audio file playback now uses useAudioPlayback hook which
        // surfaces errors visibly instead of the bare console.warn (rules/02).
        if (audioUrl) {
            setAudioError(null);
            setIsPlaying(true);
            startWaveAnimation();
            await audioPlayback.play();
            // The hook manages error state — if it fails, audioPlayback.error
            // is set and we surface it via the error fallback below.
            if (audioPlayback.error) {
                setIsPlaying(false);
                stopWaveAnimation();
                setAudioError('Audio unavailable — tap to retry');
            }
            return;
        }

        if (Platform.OS === 'web' && typeof globalThis !== 'undefined' && (globalThis as any).speechSynthesis) {
            const synth = (globalThis as any).speechSynthesis;
            synth.cancel();
            const utterance = new (globalThis as any).SpeechSynthesisUtterance(text);
            utterance.rate = 0.8;
            utterance.onend = () => {
                setIsPlaying(false);
                stopWaveAnimation();
            };
            utterance.onerror = () => {
                setIsPlaying(false);
                stopWaveAnimation();
            };
            synth.speak(utterance);
            return;
        }

        if (Platform.OS !== 'web') {
            const doSpeak = () => {
                try {
                    Tts.stop();
                    Tts.setDefaultRate(0.65);
                    const finishSub = (Tts as any).addEventListener('tts-finish', () => {
                        setIsPlaying(false);
                        stopWaveAnimation();
                        finishSub?.remove?.();
                    });
                    const cancelSub = (Tts as any).addEventListener('tts-cancel', () => {
                        setIsPlaying(false);
                        stopWaveAnimation();
                        cancelSub?.remove?.();
                    });
                    Tts.speak(text);
                } catch (speakError) {
                    console.warn(`${TTS_LOG} speak failed:`, speakError);
                    setIsPlaying(false);
                    stopWaveAnimation();
                }
            };

            if (ttsReadyRef.current) {
                doSpeak();
            } else {
                setIsLoading(true);
                Tts.getInitStatus()
                    .then(() => {
                        ttsReadyRef.current = true;
                        setIsLoading(false);
                        doSpeak();
                    })
                    .catch(async (err) => {
                        console.warn(`${TTS_LOG} tap getInitStatus() failed:`, err?.code, err?.message);
                        logTtsDiagnostics('tap_failed');
                        const recovered = await tryRecoverTtsEngine();
                        if (recovered) {
                            ttsReadyRef.current = true;
                            console.log(`${TTS_LOG} tap: recovered, speaking`);
                            doSpeak();
                            return;
                        }
                        console.log(`${TTS_LOG} tap: showing install help dialog`);
                        showTtsInstallHelp(err?.code);
                        setIsLoading(false);
                        setIsPlaying(false);
                        stopWaveAnimation();
                    });
            }
        } else {
            setTimeout(() => {
                setIsPlaying(false);
                stopWaveAnimation();
            }, 2000);
        }
    };

    useEffect(() => {
        if (autoPlay && !autoPlayedRef.current) {
            autoPlayedRef.current = true;
            playAudio();
        }
    }, [autoPlay]);

    const handlePress = () => {
        if (isLoading) return;
        // WP-055: If there's an audio error, retry instead of playing
        if (audioError && audioUrl) {
            setAudioError(null);
            audioPlayback.retry();
            return;
        }
        if (isPlaying) {
            setIsPlaying(false);
            stopWaveAnimation();
            if (audioUrl) {
                audioPlayback.stop();
            } else {
                Tts.stop();
                if (Platform.OS === 'web') (globalThis as any).speechSynthesis?.cancel?.();
            }
            return;
        }
        playAudio();
    };

    // WP-055: Sync hook error state to local state for rendering fallback
    useEffect(() => {
        if (audioPlayback.error && audioUrl) {
            setAudioError('Audio unavailable — tap to retry');
            setIsPlaying(false);
            stopWaveAnimation();
        }
    }, [audioPlayback.error, audioUrl]);

    return (
        <TouchableOpacity
            testID={testID}
            style={[
                styles.button,
                compact && styles.buttonCompact,
                isPlaying && styles.buttonActive,
                audioError && styles.buttonError,
                style,
            ]}
            onPress={handlePress}
            activeOpacity={0.8}
            accessibilityLabel={audioError ? 'Audio unavailable — tap to retry' : isPlaying ? 'Stop listening' : 'Listen'}
            accessibilityRole="button"
            accessibilityHint={audioError ? 'Double tap to retry loading audio' : 'Double tap to hear the instruction'}
        >
            <Text style={[styles.icon, compact && styles.iconCompact, audioError && styles.iconError]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                {isLoading ? '⏳' : isPlaying ? '🔊' : audioError ? '⚠' : '🔈'}
            </Text>
            {isPlaying && !isLoading && !audioError && (
                <View style={styles.waveContainer}>
                    <Animated.View style={[styles.waveBar, { opacity: wave1 }]} />
                    <Animated.View style={[styles.waveBar, { opacity: wave2 }]} />
                    <Animated.View style={[styles.waveBar, { opacity: wave3 }]} />
                </View>
            )}
            {!compact && (
                <Text
                    style={[styles.label, isPlaying && styles.labelActive, audioError && styles.labelError]}
                    allowFontScaling={true}
                    maxFontSizeMultiplier={1.5}
                >
                    {isLoading ? '...' : isPlaying ? 'Stop' : audioError ? 'Retry' : 'Listen'}
                </Text>
            )}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#EEF2FF',
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingVertical: 10,
        minHeight: 56,
        minWidth: 56,
        gap: 8,
        borderWidth: 1.5,
        borderColor: '#C7D2FE',
        shadowColor: '#6366F1',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
    },
    buttonCompact: {
        minHeight: 40,
        minWidth: 40,
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderRadius: 20,
        gap: 4,
    },
    iconCompact: {
        fontSize: 16,
    },
    buttonActive: {
        backgroundColor: '#6366F1',
        borderColor: '#4F46E5',
    },
    buttonError: {
        backgroundColor: '#FFFBE6',
        borderColor: '#FAAD14',
    },
    icon: { fontSize: 18 },
    iconError: { color: '#FAAD14' },
    label: { fontSize: 14, fontWeight: '700', color: '#4338CA' },
    labelActive: { color: '#FFFFFF' },
    labelError: { color: '#FAAD14' },
    waveContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        height: 16,
    },
    waveBar: {
        width: 4,
        height: 12,
        backgroundColor: '#4338CA',
        borderRadius: 2,
    },
});
